/**
 * JoinRequestsCard — coach-side surface for pending team-join requests.
 *
 * Auto-refreshes on mount and after each decision. Hidden entirely when
 * there are no pending requests so the card doesn't add visual noise on
 * fully-onboarded teams.
 */
import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { ChalkCard, SectionTitle } from "./primitives";

export default function JoinRequestsCard({ teamId, teamName }) {
  const [requests, setRequests] = useState([]);
  const [working, setWorking] = useState(null); // request id while decision is in-flight

  async function load() {
    if (!teamId) return;
    try {
      const data = await api.listJoinRequests(teamId);
      setRequests(data.requests || []);
    } catch {
      /* surface silently — the card just won't render */
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [teamId]);

  if (!teamId || requests.length === 0) return null;

  async function decide(req, action) {
    setWorking(req.id);
    try {
      await api.decideJoinRequest(req.id, action);
      toast.success(action === "approve"
        ? `${req.requester_first_name || "Request"} added to ${teamName || "the team"}.`
        : `Request denied.`);
      setRequests((arr) => arr.filter((r) => r.id !== req.id));
    } catch (e) {
      toast.error(e.message || "Couldn't update that request.");
    } finally {
      setWorking(null);
    }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <SectionTitle>Join requests</SectionTitle>
      {requests.map((r) => (
        <ChalkCard
          key={r.id}
          data-testid={`join-request-${r.id}`}
          style={{
            display: "flex", alignItems: "center", gap: 12,
            borderLeft: "2px solid var(--green-mid)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--chalk-board)" }}>
              {(r.requester_first_name || "Someone")} {(r.requester_last_name || "")}
            </div>
            <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {r.requester_email}
            </div>
          </div>
          <button
            type="button"
            onClick={() => decide(r, "approve")}
            disabled={working === r.id}
            data-testid={`join-approve-${r.id}`}
            aria-label="Approve"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "8px 12px", border: "none", borderRadius: 8,
              background: "var(--green-mid)", color: "#fff",
              fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 12,
              letterSpacing: "0.05em", textTransform: "uppercase",
              cursor: working === r.id ? "wait" : "pointer",
              opacity: working === r.id ? 0.6 : 1,
            }}
          >
            <Check size={14} aria-hidden="true" /> Approve
          </button>
          <button
            type="button"
            onClick={() => decide(r, "deny")}
            disabled={working === r.id}
            data-testid={`join-deny-${r.id}`}
            aria-label="Deny"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 34, height: 34, padding: 0, borderRadius: 8,
              background: "#fff", color: "var(--chalk-muted)",
              border: "0.5px solid var(--chalk-border)",
              cursor: working === r.id ? "wait" : "pointer",
              opacity: working === r.id ? 0.6 : 1,
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </ChalkCard>
      ))}
    </div>
  );
}
